import { useEffect, useId, useRef, type ReactNode } from "react";
import { useT } from "../i18n/catalogue.js";
import { useScrollLock } from "../lib/scroll-lock.js";

import { cn } from "../lib/cn.js";

/**
 * A modal dialog, built on the native `<dialog>` element.
 *
 * ==========================================================================================
 * WHY NATIVE
 * ==========================================================================================
 *
 * Native rather than a div-with-a-backdrop because the browser already implements the parts that
 * are tedious and easy to get subtly wrong: the focus trap, the inert background, Escape to close,
 * returning focus to whatever opened it, and a top layer that sits above everything without a
 * z-index arms race. A hand-rolled modal reimplements all of it, usually incompletely — and the
 * incomplete part is invariably the focus trap, which is the part a keyboard user depends on.
 *
 * `showModal()` rather than the `open` attribute: only the former gets the top layer and the focus
 * trap. Setting `open` renders a non-modal dialog that looks identical and behaves nothing like it.
 *
 * ==========================================================================================
 * IT OWNS THE FRAME AND THE HEADING, AND THE CHILD OWNS ONLY THE BODY
 * ==========================================================================================
 *
 * This division is the point of the component. Every dialog in this application is a form — add a
 * card, add a recurring expense — and those forms were each drawing their own panel border and their
 * own `<h2>`. Hosted inside a dialog that did the same, the result is two borders and the title
 * twice. So the split is: `title` and `description` come in as props and are rendered here; the
 * child supplies fields and buttons and no chrome at all.
 *
 * The copy still belongs to whoever owns the noun. `CardForm` passes its own title and its own
 * sentence about what a card is — that text is expenses' knowledge, not this package's, and it did
 * not move out of the domain package when it moved out of the markup.
 *
 * ==========================================================================================
 * THE HEADER DOES NOT SCROLL, THE BODY DOES
 * ==========================================================================================
 *
 * `max-h-[85dvh]` with the body as the scroll container, not the dialog. A recurring-expense form is
 * seven fields and a split editor; on a laptop in landscape it is taller than the viewport, and a
 * dialog that simply overflows puts its submit button somewhere unreachable. `dvh` rather than `vh`
 * because on mobile Safari `vh` is the viewport WITHOUT the browser chrome, which is the one
 * measurement guaranteed to be wrong while the URL bar is showing.
 *
 * Consequence worth knowing: the title stays visible while the fields scroll under it, which is why
 * the close control lives in the header rather than floating over the content.
 *
 * ==========================================================================================
 * WHAT IS DELIBERATELY NOT HERE
 * ==========================================================================================
 *
 * NO ENTRANCE ANIMATION. An element in the top layer cannot be transitioned in without either
 * `@starting-style` or a two-frame dance with `requestAnimationFrame`, and the payoff is 150ms of
 * polish on a control that appears in response to a click. If it is added later it needs
 * `useMotionDisabled` from `@gladis/ui/motion` — the app has a reduced-motion answer already.
 *
 * NO "ARE YOU SURE" ON DISMISSAL. Escape and the backdrop discard whatever was typed, with no
 * warning. That is the standard behaviour and the accidental case is handled below; a confirmation
 * prompt on every close would be worse than the thing it prevents.
 */
/*
 * TWO WIDTHS, AS A PROP RATHER THAN A CLASS THE CALLER PASSES.
 *
 * `cn` is a plain join, not `tailwind-merge` — see `lib/cn.ts`, which says so and says why. A caller
 * passing `max-w-4xl` through `className` would therefore not override the base width: both would
 * land in the class list and the winner would be decided by the order Tailwind happened to emit them
 * in, which is a coin toss that changes between builds. Two named sizes cannot express that bug.
 *
 *   form   the default. A form's fields, in one or two columns.
 *   wide   two panes side by side. The card form puts a live preview beside its fields, and needs
 *          about 22rem for the card plus 30rem for a two-column field grid.
 *
 * ==========================================================================================
 * THE CAP IS PER BREAKPOINT, AND `form` IS NO LONGER THE READING MEASURE
 * ==========================================================================================
 *
 * `form` was `max-w-content` — 34rem, the token `tokens.css` declares for a COLUMN OF PROSE. That is
 * the right width for a paragraph and the wrong one for a form, and the mismatch was visible the
 * moment anyone opened one on a tablet: 544px of dialog floating in the middle of a 1194px screen,
 * under half the width, reported as "the modals are very narrow".
 *
 * It is worse than merely narrow, because the field grids inside are `sm:grid-cols-2` — a VIEWPORT
 * media query, not a container one. A tablet is past the `sm` breakpoint, so the expense form drew
 * two columns inside a prose measure and gave each field about 240px. The dialog was sized for one
 * column and its contents laid out for two, and neither knew about the other.
 *
 * So the cap now grows with the viewport rather than standing still:
 *
 *   below `sm`   no cap at all — `w-[calc(100vw-2rem)]` is the whole width, which is what a phone
 *                wants and what it already got, since 34rem exceeds a phone anyway
 *   `sm` and up  the two columns have room to be two columns
 *   `lg` and up  where the old measure would have been comfortable, and the width stops growing
 *
 * The `w-[calc(100vw-2rem)]` base is unchanged and still does the real work: every cap here is a
 * MAXIMUM, so the dialog can never exceed the viewport however these are tuned.
 */
const SIZES = {
  form: "sm:max-w-[42rem] lg:max-w-[46rem]",
  wide: "sm:max-w-[46rem] lg:max-w-[56rem]",
} as const;

export type DialogSize = keyof typeof SIZES;

export interface DialogProps {
  open: boolean;
  /** Called on Escape, on backdrop dismissal, and by the close control. */
  onClose: () => void;
  /** The visible heading, and the dialog's accessible name. Required — an unnamed dialog is disorienting. */
  title: string;
  /** One sentence under the heading: what this dialog is for. Becomes the accessible description. */
  description?: ReactNode;
  /** How much room the body needs. `wide` is for a two-pane layout. */
  size?: DialogSize;
  children: ReactNode;
  className?: string;
}

export function Dialog({
  open,
  onClose,
  title,
  description,
  size = "form",
  children,
  className,
}: DialogProps): ReactNode {
  const t = useT();
  const ref = useRef<HTMLDialogElement>(null);
  const headingId = useId();
  const descriptionId = useId();

  /*
   * Distinguishes a close WE asked for from a close the MEMBER asked for.
   *
   * The browser fires one `close` event for both, and the effect below calls `close()` whenever the
   * `open` prop goes false — which happens on every successful submit. Without this flag that
   * programmatic close reports itself back through `onClose`, so a parent whose handler toggles
   * (`onClose={() => setOpen((o) => !o)}`) reopens the dialog it just finished with. Cheap to write,
   * and invisible until someone writes a toggle.
   */
  const closingOurselves = useRef(false);

  /*
   * Whether the press that started this click landed on the backdrop.
   *
   * A click whose target is the dialog element itself is a backdrop click — children sit inside the
   * inner elements, so anything else has a nearer target. But `click` fires on the common ancestor of
   * pointerdown and pointerup, so selecting the text of a label and releasing outside the panel
   * targets the dialog and looks exactly like a backdrop click. Requiring the PRESS to have started
   * on the backdrop too is what stops a drag-select from throwing away a half-filled form.
   */
  const pressStartedOnBackdrop = useRef(false);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    // Guard both ways: calling showModal on an already-open dialog throws, and calling close on a
    // closed one is a no-op that still fires a `close` event in some engines.
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) {
      closingOurselves.current = true;
      dialog.close();
    }
  }, [open]);

  /*
   * The page behind a modal must not scroll — the top layer stops the background being CLICKED, not
   * being SCROLLED.
   *
   * THIS USED TO BE `overflow: hidden` ON THE ROOT, INLINE, and that is the textbook answer and is
   * not enough: mobile Safari goes on panning the page. `useScrollLock` holds everywhere and carries
   * the scroll offset across the lock by hand, which the inline version did not have to do. See that
   * file — it is shared with the app's mobile drawer so the two cannot drift.
   *
   * The known cost, unfixed on purpose: on a platform with classic (space-occupying) scrollbars,
   * removing the scrollbar widens the viewport and the page shifts sideways as the dialog opens.
   * `scrollbar-gutter: stable` on the root would cure it, and it would also reserve that gutter on
   * every screen forever — a permanent change to every layout to fix a transient artefact on
   * platforms this household does not use.
   */
  useScrollLock(open);

  return (
    <dialog
      ref={ref}
      aria-labelledby={headingId}
      // Only when there is one: pointing `aria-describedby` at an element that was never rendered
      // leaves the dialog with a dangling reference, which some screen readers announce as empty.
      {...(description === undefined ? {} : { "aria-describedby": descriptionId })}
      // The browser fires `close` for Escape and for `close()` alike, so this is the one place
      // dismissal needs handling — no separate keydown listener.
      onClose={() => {
        if (closingOurselves.current) {
          closingOurselves.current = false;
          return;
        }
        onClose();
      }}
      onPointerDown={(event) => {
        pressStartedOnBackdrop.current = event.target === ref.current;
      }}
      onClick={(event) => {
        if (event.target === ref.current && pressStartedOnBackdrop.current) onClose();
      }}
      className={cn(
        /*
         * `open:flex`, NEVER a bare `flex`, and this is the trap in styling a `<dialog>`.
         *
         * A closed dialog is hidden by the UA rule `dialog:not([open]) { display: none }`. Author
         * styles beat the UA stylesheet regardless of specificity, so a plain `flex` utility wins
         * that contest and the dialog renders — closed, unmodal, in the flow, in the middle of the
         * page. The variant compiles to `[open]`-gated, so `showModal()` turning the attribute on is
         * what turns the layout on with it.
         *
         * `flex-col` is unguarded on purpose: it does nothing while `display` is `none`, and pairing
         * every flex utility with the variant would suggest each of them mattered.
         */
        "open:flex m-auto max-h-[85dvh] w-[calc(100vw-2rem)] flex-col",
        SIZES[size],
        // `p-0` and an explicit width, because the UA stylesheet gives `<dialog>` a `fit-content`
        // width and a padding of its own; the header and body below place their own padding.
        "p-0 rounded-control border border-input bg-page text-ink",
        "backdrop:bg-black/60",
        className,
      )}
    >
      {/* `shrink-0` so a long body cannot compress the header out of existence.

          `px-4 sm:px-6`, and the same on the body below: on a 390px phone the gutter was 24px a side
          out of 358px of dialog, which is 13% of the only width there is. The desktop padding is
          unchanged — this narrows nothing anybody has already seen. */}
      <header className="flex shrink-0 items-start justify-between gap-4 px-4 pt-6 pb-4 sm:px-6">
        <div>
          <h2 id={headingId} className="text-xl font-semibold">
            {title}
          </h2>
          {description === undefined ? null : (
            <p id={descriptionId} className="mt-1 text-sm text-muted">
              {description}
            </p>
          )}
        </div>

        {/*
          FIRST IN DOM ORDER, which decides where focus lands: `showModal()` focuses the first
          focusable descendant, so it is this button rather than the first field. That is the safer
          default — opening a dialog straight into a text input pops the software keyboard on a phone
          and hides most of the form — and it costs a keyboard user one Tab.

          A real `<button>` with a text name, not an icon with a title attribute. "Close" is what a
          screen reader announces and what the e2e suite can find.
        */}
        <button
          type="button"
          onClick={onClose}
          aria-label={t("ui.dialog.close")}
          className={
            "-mt-1 -mr-2 flex size-8 shrink-0 cursor-pointer items-center justify-center " +
            "rounded-control text-muted transition-colors hover:bg-accent hover:text-ink " +
            "focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          }
        >
          {/* Inline, because two paths are not worth a dependency on an icon set in this package. */}
          <svg
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            className="size-4"
            aria-hidden="true"
          >
            <path d="M5 5l10 10M15 5L5 15" />
          </svg>
        </button>
      </header>

      {/*
        `min-h-0` is what lets this shrink below its content height so `overflow-y-auto` engages;
        without it a flex item's floor is its content and the dialog grows past `max-h` instead.

        `flex-auto` AND NOT `flex-1`, WHICH IS A ONE-WORD DIFFERENCE WITH A REAL FAILURE BEHIND IT.
        Both grow and both shrink; they differ in FLEX BASIS — `auto` means "start from my content",
        `1` means "start from zero". With a basis of zero AND `min-h-0` this item contributes nothing
        to the dialog's intrinsic height, so the dialog's `height: auto` has to be resolved by the
        flexbox rule that reconstructs a max-content size from the growth factors. Engines implement
        that rule, and older ones implement it wrongly, and the way they get it wrong is to size the
        dialog to its header: a modal showing its title and nothing else.

        `auto` needs none of that. The dialog's intrinsic height is simply header + content, clamped
        by `max-h`, and `min-h-0` still permits the shrink that turns on the scrollbar. Identical
        rendering everywhere it already worked, and no dependence on the rule where it did not.
      */}
      {/*

        The top border is not decoration. Once the body scrolls, its first field passes under the
        header and is clipped mid-control with nothing to explain the cut — the rule turns that into
        an edge the content is visibly moving behind. It stays put when there is nothing to scroll,
        which is the ordinary anatomy of a titled dialog rather than a state to detect.
      */}
      <div className="min-h-0 flex-auto overflow-y-auto border-t border-line px-4 pt-4 pb-6 sm:px-6">
        {children}
      </div>
    </dialog>
  );
}
